/**
 * =============================================================================
 * Hook: useReferralReferralsRealtime
 * Versão: 1.0.0
 *
 * Descrição:
 *   Mantém a listagem administrativa de **Indicações** sincronizada via
 *   Supabase Realtime, sem necessidade de F5.
 *
 *   Escuta `postgres_changes` em duas tabelas:
 *     - `affiliate_referrals` — cobre criação de novas indicações, vínculo
 *       deal_id/contact_id (feito pela trigger de processamento) e mudanças
 *       diretas de status (converted/lost), preenchimento de
 *       converted_at/lost_at/conversion_value pelas triggers
 *       `trg_process_deal_won_reward` e `trg_process_deal_lost_referral`.
 *     - `crm_deals` — cobre mudanças de fase/valor que aparecem nas colunas
 *       derivadas (deal_phase_name, deal_status, deal_seller_name etc.)
 *       mesmo quando o deal está em fase intermediária e a trigger ainda não
 *       atualizou o referral.
 *
 *   Em qualquer evento, invalida a query
 *   `['referral-referrals-admin', tenantId]` para forçar refetch.
 *
 *   Segue exatamente o padrão consolidado em `useReferralAffiliatesRealtime`.
 *
 * RELEASE:
 *   2026-05-02 v1.0.0 - Criação inicial.
 * =============================================================================
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger dedicado deste hook
const logger = createLogger('ReferralReferralsRealtime');

/**
 * Subscreve mudanças relevantes para a listagem de indicações do tenant.
 *
 * @param tenantId - ID do tenant ativo (null/undefined = não subscreve)
 */
export function useReferralReferralsRealtime(
  tenantId: string | null | undefined,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    logger.debug('Iniciando Realtime de indicações', { tenantId });

    // Helper de invalidação — força refetch da listagem paginada de indicações
    const invalidate = (source: string, eventType: string) => {
      logger.debug('Realtime evento recebido — invalidando cache', {
        source,
        eventType,
      });
      queryClient.invalidateQueries({
        queryKey: ['referral-referrals-admin', tenantId],
      });
    };

    const channel = supabase
      .channel(`referral-referrals-rt-${tenantId}`)
      // Mudanças diretas em indicações (status, conversão, perda, vínculos)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliate_referrals',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => invalidate('affiliate_referrals', payload.eventType),
      )
      // Mudanças em negócios vinculados (fase, valor, vendedor, status)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deals',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => invalidate('crm_deals', payload.eventType),
      )
      .subscribe((status) => {
        logger.debug('Realtime subscription status:', status);
      });

    return () => {
      logger.debug('Removendo Realtime de indicações', { tenantId });
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}
